import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/lib/apiBase";
import { formatSlot } from "@/lib/formatSlot";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "awaiting_confirmation", label: "Awaiting Confirmation" },
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "rejected", label: "Rejected" },
];

/**
 * Modal allowing admins to edit a booking's customer details, schedule,
 * status, AND transfer to a different court. Time-slot toggles surface only
 * slots that are actually available on the target court+date (plus the
 * slots that the booking already owns, which would otherwise look "booked"
 * against itself).
 *
 * Saving issues a PATCH /api/admin/bookings/:id and triggers a customer
 * notification email summarising the changes.
 */
const EditBookingDialog = ({ booking, courts = [], onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [courtId, setCourtId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState("pending");
  const [saving, setSaving] = useState(false);

  // Per-court+date slot availability data fetched from
  // GET /api/admin/calendar/day. Populated on date/court change so the UI
  // only offers slots that are actually free on the target court.
  const [availability, setAvailability] = useState([]);  // [{time_slot, status, rate}]
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Hydrate form whenever a new booking is opened.
  useEffect(() => {
    if (!booking) return;
    setName(booking.customer_name || "");
    setEmail(booking.customer_email || "");
    setPhone(booking.customer_phone || "");
    setCourtId(booking.court_id || "");
    setDate(booking.booking_date || "");
    setSlots(Array.from(booking.time_slots || []));
    setStatus(booking.status || "pending");
  }, [booking]);

  // Refresh availability whenever the target court+date changes. Throttled
  // implicitly by React batching since both setters fire from the same UI
  // events.
  useEffect(() => {
    if (!courtId || !date) {
      setAvailability([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setAvailabilityLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/calendar/day?date=${date}`
        );
        if (cancelled) return;
        const court = (data?.courts || []).find((c) => c.court_id === courtId);
        setAvailability(court?.all_slots || []);
      } catch {
        if (!cancelled) setAvailability([]);
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courtId, date]);

  const isOriginalCourtAndDate =
    courtId === booking?.court_id && date === booking?.booking_date;

  // Build the slot list shown to the admin. A slot is selectable if:
  //   1) it's currently `available` on the target court+date, OR
  //   2) it's already part of THIS booking on its original court+date —
  //      we don't want the booking colliding with itself.
  // Slots that are booked by someone else, manually blocked, or GCal-blocked
  // are hidden so the admin can't accidentally transfer into them.
  const visibleSlots = useMemo(() => {
    if (!availability.length) return [];
    const originalSlotSet = new Set(booking?.time_slots || []);
    return availability
      .filter((s) => {
        if (s.status === "available") return true;
        // Allow the slot if it belongs to THIS booking (same court+date).
        if (isOriginalCourtAndDate && originalSlotSet.has(s.time_slot)) {
          return true;
        }
        return false;
      })
      .map((s) => ({
        time_slot: s.time_slot,
        belongsToThisBooking:
          isOriginalCourtAndDate && originalSlotSet.has(s.time_slot),
      }));
  }, [availability, booking, isOriginalCourtAndDate]);

  const toggleSlot = (slot) => {
    setSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  // Drop any currently-selected slots that are no longer visible after
  // changing court/date — prevents sending stale slot strings to the
  // backend that would fail validation.
  useEffect(() => {
    if (!availability.length) return;
    setSlots((prev) => {
      const allowed = new Set(visibleSlots.map((s) => s.time_slot));
      const filtered = prev.filter((s) => allowed.has(s));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [availability, visibleSlots]);

  // Build the diff payload — only send fields that actually changed.
  const payload = useMemo(() => {
    if (!booking) return {};
    const out = {};
    if (name.trim() !== (booking.customer_name || "")) out.customer_name = name.trim();
    if (email.trim() !== (booking.customer_email || "")) out.customer_email = email.trim();
    if (phone.trim() !== (booking.customer_phone || "")) out.customer_phone = phone.trim();
    if (courtId !== booking.court_id) out.court_id = courtId;
    if (date !== booking.booking_date) out.booking_date = date;
    const oldSlots = [...(booking.time_slots || [])].sort();
    const newSlots = [...slots].sort();
    if (oldSlots.join("|") !== newSlots.join("|")) out.time_slots = slots;
    if (status !== booking.status) out.status = status;
    return out;
  }, [booking, name, email, phone, courtId, date, slots, status]);

  const hasChanges = Object.keys(payload).length > 0;

  const handleSave = async () => {
    if (!hasChanges) {
      toast.info("No changes to save");
      return;
    }
    if (!slots.length) {
      toast.error("At least one time slot is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/bookings/${booking.id}`,
        payload
      );
      const sent = data?.customer_email_sent;
      if (sent) {
        toast.success("Booking updated. Customer notified by email.");
      } else {
        toast.warning(
          "Booking updated, but customer email failed. Check SMTP credentials."
        );
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to update booking";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="edit-booking-dialog"
        className="max-w-2xl border-2 border-white bg-[#121212] text-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            Edit Booking
            <span className="ml-2 text-xs text-[#a1a1aa] font-mono">
              #{booking.id.slice(0, 8)}
            </span>
          </DialogTitle>
          <p className="text-xs text-[#a1a1aa] mt-1">
            Original court:{" "}
            <span className="text-white">{booking.court_name}</span>
            {booking.linked_court_name ? ` + ${booking.linked_court_name}` : ""}
            {" · "}
            Saving will email the customer a summary of the changes.
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Customer */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#a1a1aa]">
                Customer Name
              </span>
              <input
                data-testid="edit-customer-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#8cc63f]"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#a1a1aa]">
                Customer Email
              </span>
              <input
                data-testid="edit-customer-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#8cc63f]"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#a1a1aa]">
                Phone
              </span>
              <input
                data-testid="edit-customer-phone"
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#8cc63f]"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#a1a1aa]">
                Court
              </span>
              <select
                data-testid="edit-court-id"
                value={courtId}
                onChange={(e) => setCourtId(e.target.value)}
                className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#8cc63f]"
              >
                {courts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                {/* Fallback so the current value always renders even if not
                    in the courts list yet. */}
                {courts.find((c) => c.id === courtId) ? null : (
                  <option value={courtId}>{booking.court_name}</option>
                )}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#a1a1aa]">
                Booking Date
              </span>
              <input
                data-testid="edit-booking-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#8cc63f]"
              />
            </label>
          </div>

          {/* Time slots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-[#a1a1aa]">
                Time Slots ({slots.length} selected)
              </span>
              <button
                type="button"
                data-testid="edit-clear-slots"
                onClick={() => setSlots([])}
                className="text-[10px] text-[#a1a1aa] hover:text-red-400 uppercase tracking-wider"
              >
                Clear
              </button>
            </div>
            <p className="text-[11px] text-[#a1a1aa] mb-2">
              Showing only slots that are <span className="text-[#8cc63f]">available</span>{" "}
              on the selected court &amp; date. Slots already booked, blocked,
              or held by Google Calendar are hidden.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1">
              {availabilityLoading && (
                <div
                  data-testid="edit-availability-loading"
                  className="col-span-3 sm:col-span-6 text-center text-xs text-[#a1a1aa] py-4"
                >
                  Loading availability…
                </div>
              )}
              {!availabilityLoading && visibleSlots.length === 0 && (
                <div
                  data-testid="edit-no-slots"
                  className="col-span-3 sm:col-span-6 text-center text-xs text-red-300 py-4 border border-red-500/30 rounded-sm bg-red-500/5"
                >
                  No available slots on this court for the chosen date.
                </div>
              )}
              {!availabilityLoading &&
                visibleSlots.map(({ time_slot: slot, belongsToThisBooking }) => {
                  const selected = slots.includes(slot);
                  return (
                    <button
                      key={slot}
                      type="button"
                      data-testid={`edit-slot-${slot.replace(/[: ]/g, "")}`}
                      onClick={() => toggleSlot(slot)}
                      className={`relative text-xs px-2 py-1.5 rounded-sm border transition-colors ${
                        selected
                          ? "bg-[#8cc63f] border-[#8cc63f] text-black font-semibold"
                          : "bg-transparent border-white/10 text-[#e4e4e7] hover:border-[#8cc63f]"
                      }`}
                    >
                      {formatSlot(slot)}
                      {belongsToThisBooking && !selected && (
                        <span
                          className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-yellow-400"
                          title="Currently on this booking"
                        />
                      )}
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Status */}
          <div>
            <span className="text-xs uppercase tracking-wider text-[#a1a1aa]">
              Status
            </span>
            <div className="mt-1 flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`edit-status-${opt.value}`}
                  onClick={() => setStatus(opt.value)}
                  className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                    status === opt.value
                      ? "bg-[#8cc63f] border-[#8cc63f] text-black font-semibold"
                      : "bg-transparent border-white/10 text-[#e4e4e7] hover:border-[#8cc63f]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
          <span
            className="text-xs text-[#a1a1aa]"
            data-testid="edit-changes-count"
          >
            {hasChanges
              ? `${Object.keys(payload).length} field${
                  Object.keys(payload).length === 1 ? "" : "s"
                } changed`
              : "No changes"}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="edit-cancel-btn"
              onClick={() => onClose?.()}
              className="px-4 py-2 text-xs uppercase tracking-wider text-[#a1a1aa] hover:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="edit-save-btn"
              disabled={saving || !hasChanges}
              onClick={handleSave}
              className="px-4 py-2 text-xs uppercase tracking-wider bg-[#8cc63f] text-black font-semibold rounded-sm hover:bg-[#9ed15c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save & Notify"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditBookingDialog;
